import {
  abilityDef,
  type AbilityId,
  type AmbienceKind,
  type ClassId,
  type School,
} from '@arena/shared';

// Source files, relative to ~/Assets/CC0/Audio. `experiments/sounds/export.ts` turns each into
// assets/sounds/<id>-<n>.mp3, numbered from 1.
const TOM = 'Free Fantasy SFX Pack By TomMusic/WAV Files/SFX/';
const tom = (name: string, count: number) =>
  Array.from({ length: count }, (_, i) => `${TOM}${name} ${i + 1}.wav`);
const MAGIC = {
  fire: 'FIRE SPELL/Fire Spell Pack/48000kHz',
  ice: 'ICE SPELL/Ice Spell Pack/48000kHz',
  lightning: 'LIGHTNING SPELL/Lightning Spell Pack/48000kHz',
  holy: 'HOLY SPELL/Holy Spell Pack/48000kHz',
  dark: 'DARK SPELL/Dark Spell Pack/48000 kHz',
  earth: 'EARTH SPELL/Earth Spell Pack/48000 kHz',
  wind: 'WIND SPELL/Wind Spell Pack/48000 kHz',
  water: 'WATER SPELL/Water Spell Pack/48000 kHz',
};
const magic = (pack: keyof typeof MAGIC, name: string, count: number) =>
  Array.from(
    { length: count },
    (_, i) =>
      `Magic Spell/${MAGIC[pack]}/ogg/${name}_${String(i + 1).padStart(2, '0')}.ogg`,
  );
const kenney = (pack: string, name: string, count: number) =>
  Array.from(
    { length: count },
    (_, i) => `kenney_${pack}/Audio/${name}_${String(i).padStart(3, '0')}.ogg`,
  );
const STEPS = `${TOM}Footsteps/`;
const SWORD = 'Attacks/Sword Attacks Hits and Blocks/';
const BOW = 'Attacks/Bow Attacks Hits and Blocks/';

export const SOUNDS = {
  'stone-walk': tom('Footsteps/Stone/Stone Walk', 5),
  'stone-run': tom('Footsteps/Stone/Stone Run', 5),
  'stone-jump': [`${STEPS}Stone/Stone Jump.wav`],
  'stone-land': [`${STEPS}Stone/Stone Land.wav`],
  'stone-chain-walk': tom('Footsteps/Stone/Stone Chain Walk', 5),
  'stone-chain-run': tom('Footsteps/Stone/Stone Chain Run', 5),
  'stone-chain-jump': [`${STEPS}Stone/Stone Chain Jump.wav`],
  'stone-chain-land': [`${STEPS}Stone/Stone Chain Land.wav`],
  'dirt-walk': tom('Footsteps/Dirt/Dirt Walk', 5),
  'dirt-run': tom('Footsteps/Dirt/Dirt Run', 5),
  'dirt-jump': [`${STEPS}Dirt/Dirt Jump.wav`],
  'dirt-land': [`${STEPS}Dirt/Dirt Land.wav`],
  'dirt-chain-walk': tom('Footsteps/Dirt/Dirt Chain Walk', 5),
  'dirt-chain-run': tom('Footsteps/Dirt/Dirt Chain Run', 5),
  'dirt-chain-jump': [`${STEPS}Dirt/Dirt Chain Jump.wav`],
  'dirt-chain-land': [`${STEPS}Dirt/Dirt Chain Land.wav`],
  'wood-walk': tom('Footsteps/Wood/Wood Walk', 5),
  'wood-run': tom('Footsteps/Wood/Wood Run', 5),
  'wood-jump': [`${STEPS}Wood/Wood Jump.wav`],
  'wood-land': [`${STEPS}Wood/Wood Land.wav`],
  'wood-chain-walk': tom('Footsteps/Wood/Wood Chain Walk', 5),
  'wood-chain-run': tom('Footsteps/Wood/Wood Chain Run', 5),
  'wood-chain-jump': [`${STEPS}Wood/Wood Chain Jump.wav`],
  'wood-chain-land': [`${STEPS}Wood/Wood Chain Land.wav`],
  grass: kenney('impact-sounds', 'footstep_grass', 5),

  swing: tom(`${SWORD}Sword Attack`, 3),
  hit: tom(`${SWORD}Sword Impact Hit`, 3),
  blocked: tom(`${SWORD}Sword Blocked`, 3),
  parry: tom(`${SWORD}Sword Parry`, 3),
  'bow-draw': tom(`${BOW}Bow Take Out`, 1),
  'bow-shot': tom(`${BOW}Bow Attack`, 2),
  'bow-hit': tom(`${BOW}Bow Impact Hit`, 3),
  'bow-blocked': tom(`${BOW}Bow Blocked`, 3),
  punch: kenney('impact-sounds', 'impactPunch_heavy', 3),
  metal: kenney('impact-sounds', 'impactMetal_heavy', 3),
  bark: kenney('impact-sounds', 'impactWood_heavy', 3),
  thud: kenney('impact-sounds', 'impactSoft_heavy', 3),
  absorb: kenney('impact-sounds', 'impactGlass_medium', 3),
  shatter: kenney('impact-sounds', 'impactGlass_heavy', 3),
  fizzle: kenney('impact-sounds', 'impactGlass_light', 3),
  cloth: Array.from(
    { length: 4 },
    (_, i) => `kenney_rpg-audio/Audio/cloth${i + 1}.ogg`,
  ),
  click: ['kenney_rpg-audio/Audio/metalClick.ogg'],
  challenge: ['RPG_Essentials_Free/10_Battle_SFX/55_Encounter_02.wav'],
  whoosh: magic('wind', 'wind_spell_cast_air_short_whoosh', 3),
  gust: magic('wind', 'wind_spell_cast_air_rise_loud_impact', 2),
  gate: [`${TOM}Doors Gates and Chests/Gate Open.wav`],

  'spell-impact': tom('Spells/Spell Impact', 3),
  'fire-cast': tom('Spells/Firespray', 2),
  'fire-release': tom('Spells/Fireball', 3),
  'fire-instant': tom('Spells/Firebuff', 2),
  'fire-impact': magic('fire', 'fire_spell_burst_impact_short', 3),
  'frost-cast': tom('Spells/Ice Barrage', 2),
  'frost-release': tom('Spells/Ice Throw', 2),
  'frost-instant': tom('Spells/Ice Freeze', 2),
  'frost-impact': magic('ice', 'Ice_Spell_fast_impact', 3),
  'ice-block': tom('Spells/Ice Wall', 2),
  'arcane-cast': magic(
    'lightning',
    'lighting_spell_electric_loop_static_sparkle_bright',
    2,
  ),
  'arcane-release': magic(
    'lightning',
    'lighting_spell_electric_zap_blast_sparkle_bright',
    3,
  ),
  'arcane-impact': magic(
    'lightning',
    'lighting_spell_electric_impact_static_light_explode_short',
    3,
  ),
  thunder: magic(
    'lightning',
    'lighting_spell_electric_impact_thunder_static_warm',
    3,
  ),
  'holy-cast': magic('holy', 'Holy_spell_heal_cast_long', 2),
  'holy-release': magic('holy', 'Holy_spell_heal_cast_bright', 3),
  'holy-instant': magic('holy', 'Holy_spell_wave_cast', 3),
  'holy-heal': magic('holy', 'Holy_spell_heal_dust_cast_evolving', 2),
  'holy-blast': magic('holy', 'Holy_spell_blast_cast', 3),
  'holy-shield': magic('holy', 'Holy_spell_protect_cast', 3),
  'holy-power': [`Magic Spell/${MAGIC.holy}/ogg/Holy_spell_special_power.ogg`],
  'shadow-cast': magic('dark', 'dark_spell_cast_energy_riser', 3),
  'shadow-release': magic('dark', 'dark_spell_cast_dark_energy_ball', 3),
  'shadow-instant': magic('dark', 'dark_spell_cast_curse', 3),
  'shadow-impact': magic('dark', 'dark_spell_cast_charge_impact', 3),
  scream: magic('dark', 'dark_spell_cast_bats_impact', 3),
  vanish: Array.from(
    { length: 3 },
    (_, i) =>
      `Magic Spell/${MAGIC.dark}/ogg/dark_spell_cast_warp_0-00${i + 1}.ogg`,
  ),
  'nature-cast': magic('earth', 'earth_spell_cast_leaves_wind', 3),
  'nature-release': magic('earth', 'earth_spell_cast_leaves_whoosh', 3),
  'nature-heal': magic('water', 'water_spell_cast_soft_drop', 2),
  roots: magic('earth', 'earth_spell_cast_rise_ground', 3),
} satisfies Record<string, string[]>;

export type SoundId = keyof typeof SOUNDS;
export const SOUND_IDS = Object.keys(SOUNDS) as SoundId[];
export const soundFile = (id: SoundId, variant: number) =>
  `/sounds/${id}-${variant + 1}.mp3`;

// Exported whole, in stereo, to assets/sounds/loop-<id>.mp3.
const BGS = 'Free Fantasy SFX Pack By TomMusic/OGG Files/BGS Loops/';
export const LOOPS = {
  'forest-day': `${BGS}Forest Day/Forest Day.ogg`,
  'forest-night': `${BGS}Forest Night/Forest Night.ogg`,
  beach: `${BGS}Beach/Beach.ogg`,
  cave: `${BGS}Cave/Cave.ogg`,
};
export type LoopId = keyof typeof LOOPS;
export const LOOP_IDS = Object.keys(LOOPS) as LoopId[];
export const loopFile = (id: LoopId) => `/sounds/loop-${id}.mp3`;

// Owned songs, not CC0: relative to ~/Assets/Audio/Owned, exported whole to assets/sounds/music-<id>.mp3.
export const MUSIC = {
  hub: 'Appel aux Armées.m4a',
  arena: 'Raw Primal War.m4a',
};
export type MusicId = keyof typeof MUSIC;
export const MUSIC_IDS = Object.keys(MUSIC) as MusicId[];
export const musicFile = (id: MusicId) => `/sounds/music-${id}.mp3`;

/** The loop each `MapEnv.ambience` plays by day, and after dark. */
export const AMBIENCE: Record<AmbienceKind, [day: LoopId, night: LoopId]> = {
  forest: ['forest-day', 'forest-night'],
  beach: ['beach', 'beach'],
  cave: ['cave', 'cave'],
};

/** What an ability sounds like: while casting, as it goes off, and where it lands. */
export interface Voice {
  cast?: SoundId;
  release?: SoundId;
  impact?: SoundId;
  /** Instead of `impact` when a shield soaks the whole hit. */
  blocked?: SoundId;
}

interface SchoolVoice {
  cast?: SoundId;
  release?: SoundId;
  instant?: SoundId;
  impact?: SoundId;
  /** Instead of `impact` on a friendly target. */
  heal?: SoundId;
}

const SCHOOLS: Record<School, SchoolVoice> = {
  physical: {},
  fire: {
    cast: 'fire-cast',
    release: 'fire-release',
    instant: 'fire-instant',
    impact: 'fire-impact',
  },
  frost: {
    cast: 'frost-cast',
    release: 'frost-release',
    instant: 'frost-instant',
    impact: 'frost-impact',
  },
  arcane: {
    cast: 'arcane-cast',
    release: 'arcane-release',
    instant: 'arcane-release',
    impact: 'arcane-impact',
  },
  holy: {
    cast: 'holy-cast',
    release: 'holy-release',
    instant: 'holy-instant',
    impact: 'holy-blast',
    heal: 'holy-heal',
  },
  shadow: {
    cast: 'shadow-cast',
    release: 'shadow-release',
    instant: 'shadow-instant',
    impact: 'shadow-impact',
  },
  nature: {
    cast: 'nature-cast',
    release: 'nature-release',
    instant: 'nature-release',
    impact: 'spell-impact',
    heal: 'nature-heal',
  },
};

const MELEE: Voice = { release: 'swing', impact: 'hit', blocked: 'blocked' };
const SHOT: Voice = {
  cast: 'bow-draw',
  release: 'bow-shot',
  impact: 'bow-hit',
  blocked: 'bow-blocked',
};
/** Abilities fired from the bow, whatever their school. */
const SHOTS = new Set<AbilityId>([
  'steady-shot',
  'arcane-shot',
  'aimed-shot',
  'serpent-sting',
  'concussive-shot',
  'scatter-shot',
  'silencing-shot',
  'tranquilizing-shot',
]);

const VOICES: Partial<Record<AbilityId, Voice>> = {
  swap: { release: 'whoosh' },
  'battle-cry': { release: 'scream' },
  intercept: { release: 'whoosh' },
  bloodlust: { release: 'fire-instant' },
  'piercing-roar': { release: 'gust' },
  'defensive-stance': { release: 'metal' },
  shadowstep: { release: 'cloth' },
  adrenaline: { release: 'whoosh' },
  ambush: { release: 'cloth' },
  'smoke-bomb': { release: 'metal' },
  feint: { release: 'vanish' },
  'lock-and-load': { release: 'bow-draw' },
  'rapid-fire': { release: 'bow-draw' },
  'parting-leap': { release: 'whoosh' },
  'parting-trap': { release: 'metal' },
  'pvp-trinket': { release: 'holy-power' },
  bandage: { cast: 'cloth' },
  'healing-potion': { release: 'nature-heal' },
  'regeneration-potion': { release: 'nature-heal' },
  refreshment: { cast: 'cloth' },
  charge: { release: 'whoosh' },
  'intimidating-shout': { release: 'gust' },
  'berserker-rage': { release: 'fire-instant' },
  'shield-wall': { release: 'metal' },
  'spell-reflection': { release: 'metal' },
  'ice-block': { release: 'ice-block' },
  'power-word-shield': { release: 'holy-shield' },
  'pain-suppression': { release: 'holy-shield' },
  'divine-shield': { release: 'holy-shield' },
  'blessing-of-freedom': { release: 'holy-shield' },
  'hammer-of-justice': { impact: 'punch' },
  'psychic-scream': { release: 'scream' },
  'cheap-shot': { impact: 'punch' },
  'kidney-shot': { impact: 'punch' },
  stealth: { release: 'cloth' },
  vanish: { release: 'vanish' },
  sprint: { release: 'whoosh' },
  disengage: { release: 'whoosh' },
  'jaw-trap': { release: 'metal', impact: 'metal' },
  'entangling-roots': { impact: 'roots' },
  cyclone: { impact: 'gust' },
  barkskin: { release: 'bark' },
  'lightning-bolt': {
    cast: 'arcane-cast',
    release: 'arcane-release',
    impact: 'thunder',
  },
  'chain-lightning': {
    cast: 'arcane-cast',
    release: 'arcane-release',
    impact: 'thunder',
  },
  'wind-shear': { release: 'gust' },
  'healing-stream-totem': { release: 'roots' },
  'earthbind-totem': { release: 'roots' },
  'tremor-totem': { release: 'roots' },
  'grounding-totem': { release: 'roots' },
  'piercing-howl': { release: 'gust' },
  'war-banner': { release: 'metal' },
  blind: { release: 'whoosh' },
  'cloak-of-shadows': { release: 'vanish' },
  caltrops: { release: 'metal' },
  'freezing-trap': { release: 'metal', impact: 'ice-block' },
  'mighty-bash': { impact: 'punch' },
  thornfield: { release: 'roots' },
  'spirit-lantern': { release: 'roots' },
};

export function voice(id: AbilityId): Voice {
  const ability = abilityDef(id);
  if (SHOTS.has(id)) return { ...SHOT, ...VOICES[id] };
  if (ability.target === 'enemy' && !ability.range)
    return { ...MELEE, ...VOICES[id] };
  const school = SCHOOLS[ability.school];
  const timed = !!ability.castTime || !!ability.channel;
  return {
    cast: school.cast,
    release: timed ? school.release : school.instant,
    impact:
      ability.target === 'friendly'
        ? (school.heal ?? school.impact)
        : school.impact,
    blocked: 'absorb',
    ...VOICES[id],
  };
}

export type Surface = 'stone' | 'dirt' | 'wood' | 'grass';
export type Footfall = 'walk' | 'run' | 'jump' | 'land';

/** First matching piece-id prefix wins; anything else is stone. */
const SURFACES: [string, Surface][] = [
  // Mini Arena's sand.
  ['floor', 'dirt'],
  ['pirate/patch-grass', 'grass'],
  ['pirate/rocks-sand', 'dirt'],
  ['pirate/rocks', 'stone'],
  ['pirate/', 'wood'],
  ['dungeon/barrel', 'wood'],
  ['dungeon/chest', 'wood'],
];

/** The surface of a map piece, or of the terrain (green reads as grass, anything else as dirt). */
export function surfaceOf(
  piece: string | null,
  terrainColor = '#7da35a',
): Surface {
  if (piece === null) {
    const [r, g] = [1, 3].map((i) =>
      parseInt(terrainColor.slice(i, i + 2), 16),
    );
    return g > r ? 'grass' : 'dirt';
  }
  return SURFACES.find(([prefix]) => piece.startsWith(prefix))?.[1] ?? 'stone';
}

/** Plate wearers jingle as they move. */
const PLATE = new Set<ClassId>(['warrior', 'paladin']);

export function footfallSound(
  surface: Surface,
  footfall: Footfall,
  classId: ClassId,
): SoundId {
  if (surface === 'grass')
    return footfall === 'walk' || footfall === 'run'
      ? 'grass'
      : footfallSound('dirt', footfall, classId);
  const chain = PLATE.has(classId) ? '-chain' : '';
  return `${surface}${chain}-${footfall}` as SoundId;
}
